import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { LockKeyhole } from 'lucide-react'

import { UNAUTHORIZED_EVENT } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'

/**
 * The studio's front door (`app/api/auth.py`). A server with no password configured answers
 * `auth_enabled: false` and the gate is invisible; otherwise nothing behind it mounts — not the
 * router, not the SSE bridge — until `/api/auth/me` says this browser holds a session.
 *
 * A 401 from any later request (the session ran out) fires `UNAUTHORIZED_EVENT` and the gate
 * drops back to the sign-in form without a reload.
 */

type Phase = 'checking' | 'signed-out' | 'signed-in' | 'offline'

interface Me {
  authenticated: boolean
  auth_enabled: boolean
  username: string | null
}

async function fetchMe(): Promise<Me> {
  const res = await fetch('/api/auth/me', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as Me
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('checking')

  const check = useCallback(async () => {
    try {
      const me = await fetchMe()
      setPhase(me.authenticated ? 'signed-in' : 'signed-out')
    } catch {
      setPhase('offline')
    }
  }, [])

  useEffect(() => {
    void check()
    const onUnauthorized = () => setPhase('signed-out')
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [check])

  if (phase === 'signed-in') return <>{children}</>
  if (phase === 'checking') {
    return (
      <p className="grid min-h-screen place-items-center text-sm text-ink-2" role="status">
        Opening the studio…
      </p>
    )
  }
  if (phase === 'offline') {
    return (
      <div className="grid min-h-screen place-items-center px-4">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <p className="text-base font-medium text-ink">The studio server is not answering.</p>
          <p className="text-sm text-ink-2">It may be restarting after an update. Try again in a moment.</p>
          <Button variant="primary" onClick={() => void check()}>
            Try again
          </Button>
        </div>
      </div>
    )
  }
  return <SignIn onSignedIn={() => setPhase('signed-in')} />
}

function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ username, password }),
      })
      if (res.ok) {
        setPassword('')
        onSignedIn()
        return
      }
      if (res.status === 429) {
        const wait = Number(res.headers.get('Retry-After') ?? 0)
        setError(`Too many failed attempts. Try again in ${Math.max(1, Math.ceil(wait / 60))} min.`)
      } else if (res.status === 401) {
        setError('Wrong username or password.')
      } else {
        setError(`The server answered ${res.status}. Try again in a moment.`)
      }
    } catch {
      setError('Could not reach the studio server.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-paper-2 px-4 py-10">
      <form
        onSubmit={submit}
        className="flex w-full max-w-sm flex-col gap-5 rounded-2xl border border-line bg-paper p-6 shadow-sm sm:p-8"
        aria-labelledby="studio-signin-title"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
            <LockKeyhole className="size-6" aria-hidden />
          </span>
          <h1 id="studio-signin-title" className="text-xl font-semibold text-ink">
            Story Studio
          </h1>
          <p className="text-sm text-ink-2">Sign in to make, annotate and review stories.</p>
        </div>
        <Field label="Username" htmlFor="studio-username">
          <Input
            id="studio-username"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            aria-invalid={error ? true : undefined}
          />
        </Field>
        <Field label="Password" htmlFor="studio-password" error={error ?? undefined}>
          <Input
            id="studio-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error ? true : undefined}
          />
        </Field>
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!username || !password}>
          Sign in
        </Button>
      </form>
    </main>
  )
}

/** Ends the session and returns to the sign-in form. */
export async function signOut(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
  } finally {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
  }
}
